import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Tabs,
  Typography,
  message,
} from 'antd'
import { ArrowLeftOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import { AgentDebugChat, type AgentDebugMessage, type OrchestrationStep } from './AgentDebugChat'
import type { AiAgentRow } from './AgentList'
import './AgentSettings.css'

type LlmProvider = {
  id: string
  label: string
  chat_models: string[]
  supports_chat: boolean
}

type SkillOption = {
  skill_code: string
  skill_name: string
  description?: string
}

type AgentOption = {
  agent_code: string
  agent_name: string
}

type ChatMsg = AgentDebugMessage

type DebugResult = {
  reply: string
  reasoning?: string | null
  model?: string
  provider?: string
  orchestration_steps?: OrchestrationStep[]
  orchestrated?: boolean
}

const PROMPT_FIELD_BY_TAB: Record<string, string> = {
  'zh-CN': 'prompt_zh_cn',
  en: 'prompt_en',
  'zh-TW': 'prompt_zh_tw',
}

export function AgentEditor() {
  const { agentId: agentIdParam } = useParams()
  const isNew = agentIdParam === 'new' || agentIdParam == null
  const agentId = isNew ? null : Number(agentIdParam)
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [providers, setProviders] = useState<LlmProvider[]>([])
  const [globalModel, setGlobalModel] = useState('')
  const [skills, setSkills] = useState<SkillOption[]>([])
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([])
  const [pickSkill, setPickSkill] = useState<string>()
  const [promptTab, setPromptTab] = useState('zh-CN')
  const [debugLocale, setDebugLocale] = useState('zh-CN')
  const [debugInput, setDebugInput] = useState('')
  const [debugMsgs, setDebugMsgs] = useState<ChatMsg[]>([])
  const [orchestrationSteps, setOrchestrationSteps] = useState<OrchestrationStep[]>([])
  const [debugging, setDebugging] = useState(false)
  const codeTimer = useRef<number | null>(null)

  const chatProvider = Form.useWatch('chat_provider', form) || ''
  const orchestrationMode = Form.useWatch('orchestration_mode', form) || 'none'
  const subAgentCodes = (Form.useWatch('sub_agent_codes', form) as string[] | undefined) || []
  const providerDef = useMemo(
    () => providers.find((p) => p.id === chatProvider),
    [providers, chatProvider],
  )

  const loadProviders = useCallback(() => {
    return adminApi
      .get<{ providers?: LlmProvider[] }>('/admin/settings/model/providers')
      .then((r) => setProviders((r.data.providers || []).filter((p) => p.supports_chat)))
      .catch(() => setProviders([]))
  }, [])

  const loadGlobalModel = useCallback(() => {
    return adminApi
      .get<{ chat_model?: string }>('/admin/settings/model')
      .then((r) => setGlobalModel(String(r.data.chat_model || '')))
      .catch(() => undefined)
  }, [])

  const loadSkills = useCallback(() => {
    return adminApi
      .get<{ items: SkillOption[] }>('/admin/agents/skills/active')
      .then((r) => setSkills(r.data.items || []))
      .catch(() => setSkills([]))
  }, [])

  const loadAgentOptions = useCallback(() => {
    return adminApi
      .get<{ items: AgentOption[] }>('/admin/agents/options', { params: { active_only: true, page_size: 100 } })
      .then((r) => setAgentOptions(r.data.items || []))
      .catch(() => setAgentOptions([]))
  }, [])

  const loadAgent = useCallback(() => {
    if (!agentId || !Number.isFinite(agentId)) return Promise.resolve()
    setLoading(true)
    return adminApi
      .get<AiAgentRow>(`/admin/agents/${agentId}`)
      .then((r) => {
        form.setFieldsValue({
          ...r.data,
          chat_provider: r.data.chat_provider || undefined,
          chat_model: r.data.chat_model || undefined,
          sub_agent_codes: r.data.sub_agent_codes || [],
          orchestration_mode: r.data.orchestration_mode || 'none',
          max_sub_agent_steps: r.data.max_sub_agent_steps ?? 3,
        })
      })
      .catch((e) => {
        message.error(e.message)
        navigate('/manage/agents', { replace: true })
      })
      .finally(() => setLoading(false))
  }, [agentId, form, navigate])

  useEffect(() => {
    void loadProviders()
    void loadGlobalModel()
    void loadSkills()
    void loadAgentOptions()
  }, [loadProviders, loadGlobalModel, loadSkills, loadAgentOptions])

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        temperature: 0.3,
        sort_no: 0,
        is_active: true,
        prompt_zh_cn: '',
        prompt_en: '',
        prompt_zh_tw: '',
        sub_agent_codes: [],
        orchestration_mode: 'none',
        max_sub_agent_steps: 3,
      })
      return
    }
    void loadAgent()
  }, [isNew, loadAgent, form])

  const suggestCode = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) {
      form.setFieldValue('agent_code', '')
      return
    }
    adminApi
      .get<{ agent_code: string }>('/admin/agents/suggest-code', { params: { name: trimmed } })
      .then((r) => form.setFieldValue('agent_code', r.data.agent_code))
      .catch(() => undefined)
  }

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isNew) return
    const name = e.target.value
    if (codeTimer.current) window.clearTimeout(codeTimer.current)
    codeTimer.current = window.setTimeout(() => suggestCode(name), 300)
  }

  const insertSkill = () => {
    const code = (pickSkill || '').trim()
    if (!code) {
      message.warning('请选择 Skill')
      return
    }
    const field = PROMPT_FIELD_BY_TAB[promptTab] || 'prompt_zh_cn'
    const cur = String(form.getFieldValue(field) || '')
    const token = `{{skill:${code}}}`
    form.setFieldValue(field, cur ? `${cur}\n${token}` : token)
    message.success(`已插入 ${token}`)
  }

  const save = () => {
    void form.validateFields().then((v) => {
      setSaving(true)
      const body = {
        agent_name: v.agent_name,
        description: v.description || '',
        prompt_zh_cn: v.prompt_zh_cn || '',
        prompt_en: v.prompt_en || '',
        prompt_zh_tw: v.prompt_zh_tw || '',
        sub_agent_codes: v.sub_agent_codes || [],
        orchestration_mode: v.orchestration_mode || 'none',
        max_sub_agent_steps: v.max_sub_agent_steps ?? 3,
        chat_provider: v.chat_provider || '',
        chat_model: v.chat_model || '',
        temperature: v.temperature ?? 0.3,
        sort_no: v.sort_no ?? 0,
        is_active: v.is_active ?? true,
        ...(isNew ? { agent_code: v.agent_code || undefined } : {}),
      }
      const req = isNew
        ? adminApi.post<AiAgentRow>('/admin/agents', body)
        : adminApi.put<AiAgentRow>(`/admin/agents/${agentId}`, body)
      return req
        .then((r) => {
          message.success('已保存')
          if (isNew) navigate(`/manage/agents/${r.data.id}`, { replace: true })
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSaving(false))
    })
  }

  const draftPayload = () => {
    const v = form.getFieldsValue(true) as Record<string, unknown>
    return {
      prompt_zh_cn: v.prompt_zh_cn,
      prompt_en: v.prompt_en,
      prompt_zh_tw: v.prompt_zh_tw,
      sub_agent_codes: v.sub_agent_codes || [],
      orchestration_mode: v.orchestration_mode || 'none',
      max_sub_agent_steps: v.max_sub_agent_steps ?? 3,
      chat_provider: v.chat_provider || '',
      chat_model: v.chat_model || '',
      temperature: v.temperature,
    }
  }

  const sendDebug = () => {
    const text = debugInput.trim()
    if (!text || debugging) return
    const nextMsgs: ChatMsg[] = [...debugMsgs, { role: 'user', content: text }]
    setDebugInput('')
    setDebugMsgs(nextMsgs)
    setOrchestrationSteps([])
    setDebugging(true)
    const url = agentId ? `/admin/agents/${agentId}/debug` : '/admin/agents/debug'
    adminApi
      .post<DebugResult>(url, {
        locale: debugLocale,
        messages: nextMsgs,
        ...draftPayload(),
      })
      .then((r) => {
        setDebugMsgs([...nextMsgs, { role: 'assistant', content: r.data.reply }])
        setOrchestrationSteps(r.data.orchestration_steps || [])
      })
      .catch((e) => {
        setDebugMsgs(nextMsgs)
        message.error(e.message)
      })
      .finally(() => setDebugging(false))
  }

  const chatModelOptions = providerDef?.chat_models?.length
    ? providerDef.chat_models.map((m) => ({ value: m, label: m }))
    : globalModel
      ? [{ value: globalModel, label: `${globalModel}（全局）` }]
      : []

  const agentName = Form.useWatch('agent_name', form) as string | undefined
  const agentCode = Form.useWatch('agent_code', form) as string | undefined

  const subAgentSelectOptions = useMemo(() => {
    const self = (agentCode || '').trim()
    return agentOptions
      .filter((a) => a.agent_code !== self)
      .map((a) => ({
        value: a.agent_code,
        label: `${a.agent_name}（${a.agent_code}）`,
      }))
  }, [agentOptions, agentCode])

  if (!isNew && (!Number.isFinite(agentId) || (agentId ?? 0) <= 0)) {
    return (
      <div className="agent-editor">
        <Typography.Text type="secondary">无效的智能体 ID</Typography.Text>
        <div style={{ marginTop: 12 }}>
          <Button type="link" onClick={() => navigate('/manage/agents')}>
            返回智能体列表
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="agent-editor">
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        style={{ paddingLeft: 0, marginBottom: 8 }}
        onClick={() => navigate('/manage/agents')}
      >
        返回智能体列表
      </Button>

      <Row gutter={16} className="agent-editor-layout">
        <Col xs={24} lg={14}>
          <Card title={isNew ? '新建智能体' : '编辑智能体'} loading={loading}>
            <Form form={form} layout="vertical">
              <Form.Item
                name="agent_name"
                label="智能体名称"
                rules={[{ required: true, message: '请输入名称' }]}
              >
                <Input placeholder="如 客服助手、合同审阅" onChange={onNameChange} />
              </Form.Item>
              <Form.Item
                name="agent_code"
                label="标识"
                extra={isNew ? '根据名称自动生成，保存后不可修改' : '内置智能体标识不可修改'}
              >
                <Input disabled placeholder="保存前自动生成" />
              </Form.Item>
              <Form.Item name="description" label="说明">
                <Input.TextArea rows={2} placeholder="适用场景（可选）" />
              </Form.Item>

              <div className="agent-form-section">
                <div className="agent-form-section-head">
                  <Typography.Text strong>提示词</Typography.Text>
                  <Typography.Text type="secondary" className="agent-form-section-desc">
                    按语言分别配置；可在正文中插入 Skill 占位符，运行时自动展开。
                  </Typography.Text>
                </div>
                <div className="agent-prompt-skill-bar">
                  <Select
                    allowClear
                    placeholder="选择 Skill"
                    className="agent-prompt-skill-select"
                    value={pickSkill}
                    onChange={setPickSkill}
                    options={skills.map((s) => ({
                      value: s.skill_code,
                      label: s.skill_name,
                    }))}
                  />
                  <Button icon={<PlusOutlined />} onClick={insertSkill}>
                    插入
                  </Button>
                  <Typography.Text type="secondary" className="agent-prompt-skill-hint">
                    <code>{'{{skill:code}}'}</code>
                  </Typography.Text>
                  <Link to="/manage/agents/skills" className="agent-prompt-skill-link">
                    <LinkOutlined /> Skill 库
                  </Link>
                </div>
                <Tabs
                  className="agent-editor-prompt-tabs"
                  activeKey={promptTab}
                  onChange={setPromptTab}
                  items={[
                    {
                      key: 'zh-CN',
                      label: '简体中文',
                      children: (
                        <Form.Item name="prompt_zh_cn" style={{ marginBottom: 0 }}>
                          <Input.TextArea rows={14} placeholder="系统提示词（简体）" />
                        </Form.Item>
                      ),
                    },
                    {
                      key: 'en',
                      label: 'English',
                      children: (
                        <Form.Item name="prompt_en" style={{ marginBottom: 0 }}>
                          <Input.TextArea rows={14} placeholder="System prompt (English)" />
                        </Form.Item>
                      ),
                    },
                    {
                      key: 'zh-TW',
                      label: '繁體中文',
                      children: (
                        <Form.Item name="prompt_zh_tw" style={{ marginBottom: 0 }}>
                          <Input.TextArea rows={14} placeholder="系統提示詞（繁體）" />
                        </Form.Item>
                      ),
                    },
                  ]}
                />
              </div>

              <Divider style={{ margin: '20px 0' }} />

              <div className="agent-form-section">
                <div className="agent-form-section-head">
                  <Typography.Text strong>编排子智能体</Typography.Text>
                  <Typography.Text type="secondary" className="agent-form-section-desc">
                    开启 Supervisor 后，调试时主智能体可按需调用下列子智能体（最多 5 步）。
                  </Typography.Text>
                </div>
                <Row gutter={12}>
                  <Col span={24}>
                    <Form.Item name="sub_agent_codes" label="子智能体" extra="按选择顺序依次排列">
                      <Select
                        mode="multiple"
                        allowClear
                        placeholder="选择可调用的子智能体"
                        options={subAgentSelectOptions}
                        optionFilterProp="label"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="orchestration_mode" label="编排模式">
                      <Select
                        options={[
                          { value: 'none', label: '关闭' },
                          { value: 'supervisor', label: 'Supervisor' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="max_sub_agent_steps" label="最大步数">
                      <InputNumber
                        min={1}
                        max={5}
                        style={{ width: '100%' }}
                        disabled={orchestrationMode === 'none'}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                {orchestrationMode === 'supervisor' && subAgentCodes.length > 0 ? (
                  <Typography.Text type="secondary" className="agent-form-section-footnote">
                    已选 {subAgentCodes.length} 个子智能体，保存后在右侧调试可查看编排过程。
                  </Typography.Text>
                ) : null}
              </div>

              <Divider style={{ margin: '20px 0' }} />

              <div className="agent-form-section">
                <div className="agent-form-section-head">
                  <Typography.Text strong>模型设置</Typography.Text>
                  <Typography.Text type="secondary" className="agent-form-section-desc">
                    留空则使用<Link to="/manage/models">模型管理</Link>中的全局对话模型（当前：
                    {globalModel || '未配置'}）。
                  </Typography.Text>
                </div>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="chat_provider" label="厂商">
                    <Select
                      allowClear
                      placeholder="跟随全局"
                      options={providers.map((p) => ({ value: p.id, label: p.label }))}
                      onChange={() => form.setFieldValue('chat_model', undefined)}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="chat_model" label="模型">
                    <Select
                      allowClear
                      placeholder="跟随全局"
                      showSearch
                      options={chatModelOptions}
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="temperature" label="温度">
                    <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="sort_no" label="排序">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="is_active" label="启用" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
              </div>

              <Button type="primary" onClick={save} loading={saving} style={{ marginTop: 8 }}>
                保存
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <div className="agent-editor-debug-wrap">
            <AgentDebugChat
              agentName={agentName}
              agentCode={agentCode}
              locale={debugLocale}
              onLocaleChange={setDebugLocale}
              messages={debugMsgs}
              input={debugInput}
              onInputChange={setDebugInput}
              onSend={sendDebug}
              onClear={() => {
                setDebugMsgs([])
                setDebugInput('')
                setOrchestrationSteps([])
              }}
              loading={debugging}
              orchestrationSteps={orchestrationSteps}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}
